import React, { useMemo, useState, useCallback } from 'react'
import { Button, Icon, Popup, Loader } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import createAction from '../../../../../../../redux/actions/createAction'
import { FETCH_BOL_PDF_DOWNLOAD } from '../../../../../../../redux/actions/actionTypes'

const DownloadBulkBOL = ({ dispatch, shipmentObjs }) => {
  const { t } = useTranslation('common')
  const [loadingFailed, setLoadingFailed] = useState(false)
  const [loading, setLoading] = useState(false)
  const [justDownloaded, setJustDownloaded] = useState(false)

  const shipmentIds = useMemo(
    () =>
      Object.keys(shipmentObjs || {})
        .map(k => shipmentObjs[k]?.id)
        .filter(Boolean),
    [shipmentObjs]
  )

  const download = useCallback(responseData => {
    if (!responseData?.contents) return
    try {
      const blob = dataURItoBlob(responseData.contents)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.download = responseData.fileName || 'bol.pdf'
      a.href = url
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      setJustDownloaded(true)
    } catch (err) {
      setLoadingFailed(true)
    }
  }, [])

  const handleClick = useCallback(async () => {
    setLoading(true)
    setJustDownloaded(false)
    setLoadingFailed(false)
    try {
      await Promise.all(
        shipmentIds.map(
          shipmentId =>
            new Promise(resolve => {
              try {
                dispatch(
                  createAction(FETCH_BOL_PDF_DOWNLOAD, {
                    shipmentId,
                    onFailure: () => {
                      setLoadingFailed(true)
                      resolve()
                    },
                    onSuccess: responseData => {
                      download(responseData)
                      resolve()
                    },
                  })
                )
              } catch {
                setLoadingFailed(true)
                resolve()
              }
            })
        )
      )
    } finally {
      setLoading(false)
    }
  }, [dispatch, download, shipmentIds])

  const tooltip = loadingFailed
    ? t('Bulk BOL download failed. Try again')
    : justDownloaded
      ? t('PDF downloaded')
      : t('Click to Download')

  return (
    <Popup
      pinned={loadingFailed}
      content={tooltip}
      trigger={
        <Button
          color="green"
          type="button"
          disabled={loading || shipmentIds.length === 0}
          onClick={handleClick}
          style={{ margin: '1rem 0 0 0' }}
          aria-busy={loading}
          aria-live="polite"
        >
          <Button.Content visible>
            <span>{loading ? t('Please wait while BOL downloads') : t('Bulk BOL')}</span>
            {loading ? (
              <Loader inline active size="mini" style={{ margin: '0 0 0 0.5rem' }} />
            ) : (
              <Icon name="download" style={{ margin: '0 0 0 0.5rem' }} />
            )}
          </Button.Content>
        </Button>
      }
    />
  )
}

export default DownloadBulkBOL

function dataURItoBlob(base64) {
  const byteString = window.atob(base64)
  const arrayBuffer = new ArrayBuffer(byteString.length)
  const int8Array = new Uint8Array(arrayBuffer)
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < byteString.length; i++) int8Array[i] = byteString.charCodeAt(i)
  return new Blob([int8Array], { type: 'application/pdf' })
}
